import isLinked from '@utils/sceneObjects/assertions/isLinked'
import { LinkedItem } from '@types'
import { rebuildLinkedItemZonesFromUnit } from '@utils/sceneObjects/linked/rebuildLinkedItemZonesFromUnit'
import type { Object3D } from 'three'

type SceneGlbEntry = {
  glb?: { scene?: { children?: unknown }; children?: unknown } | Object3D
  backGLBInfo?: {
    acabados?: Array<{
      idAcabadoUnidad?: number
      zona?: string
      descripcion?: string
    }>
    codigo?: string
  }
}

function collectGlbMeshNames(glb: SceneGlbEntry['glb']): Set<string> {
  const names = new Set<string>()
  if (!glb) return names

  const root = (glb as { scene?: Object3D }).scene ?? (glb as Object3D)
  if (!root || typeof (root as Object3D).traverse !== 'function') return names

  ;(root as Object3D).traverse((obj) => {
    const mesh = obj as Object3D & { isMesh?: boolean; name?: string }
    if (mesh.isMesh && mesh.name) names.add(String(mesh.name))
  })

  return names
}

function getGlbSceneChildren(glb: SceneGlbEntry['glb']): unknown {
  if (!glb) return undefined
  const withScene = glb as { scene?: { children?: unknown }; children?: unknown }
  return withScene.scene?.children ?? withScene.children
}

/**
 * True when saved texture/zone mesh keys no longer exist in the current GLB
 * (e.g. RA905 Cube002_* → Cube001_* after catalog asset update).
 */
export function linkedItemHasStaleGlbZoneNames(
  item: Pick<LinkedItem, 'textures' | 'zones'>,
  glbMeshNames: Set<string>,
): boolean {
  if (glbMeshNames.size === 0) return false

  const textureZoneNames = (item.textures ?? [])
    .map((t) => (typeof t?.zoneName === 'string' ? t.zoneName.trim() : ''))
    .filter(Boolean)

  if (textureZoneNames.length > 0) {
    const matched = textureZoneNames.filter((name) => glbMeshNames.has(name)).length
    return matched === 0
  }

  const zoneKeys = Object.keys(item.zones ?? {}).filter((key) =>
    /^(Mesh|Cube)\d+/i.test(key),
  )
  if (zoneKeys.length === 0) return false
  return zoneKeys.every((key) => !glbMeshNames.has(key))
}

function buildAcabadosZonasExtra(
  acabados: NonNullable<SceneGlbEntry['backGLBInfo']>['acabados'],
): { zonas: { idAcabadoUnidad: number; zona: string }[] } | undefined {
  if (!Array.isArray(acabados) || acabados.length === 0) return undefined
  const zonas = acabados
    .filter((a) => a && a.idAcabadoUnidad && a.zona)
    .map((a) => ({
      idAcabadoUnidad: a.idAcabadoUnidad as number,
      zona: String(a.zona),
    }))
  return zonas.length > 0 ? { zonas } : undefined
}

export type RemapStaleZonesResult = {
  remappedCount: number
  remappedItemIds: string[]
}

/**
 * Reconstruye zones/textures de linkedItems cuyos nombres de mesh guardados
 * ya no existen en el GLB actual del catálogo. Preserva acabados de usuario
 * emparejando por zoneDisplayName (Puerta / Armazon / …).
 */
export function remapStaleLinkedZonesOnProjectLoad(
  items: Map<string, unknown>,
  sceneGLB: Map<string | number, SceneGlbEntry>,
): RemapStaleZonesResult {
  const result: RemapStaleZonesResult = {
    remappedCount: 0,
    remappedItemIds: [],
  }

  for (const [itemId, raw] of items.entries()) {
    if (!isLinked(raw)) continue
    const item = raw as LinkedItem
    if (!Array.isArray(item.textures) || item.textures.length === 0) continue

    const catalogKey = item.catalogID || item.idUnidades
    if (catalogKey == null || catalogKey === '') continue

    const sceneEntry =
      sceneGLB.get(catalogKey) ||
      (item.catalogID != null ? sceneGLB.get(String(item.catalogID)) : undefined) ||
      (item.idUnidades != null ? sceneGLB.get(item.idUnidades) : undefined)

    if (!sceneEntry?.glb) continue

    const meshNames = collectGlbMeshNames(sceneEntry.glb)
    if (!linkedItemHasStaleGlbZoneNames(item, meshNames)) continue

    const acabados = sceneEntry.backGLBInfo?.acabados
    if (!Array.isArray(acabados) || acabados.length === 0) continue

    const glbSceneChildren = getGlbSceneChildren(sceneEntry.glb)
    if (!glbSceneChildren) continue

    try {
      rebuildLinkedItemZonesFromUnit({
        item,
        newUnidadData: {
          acabados,
          codigo: String(item.catalogID ?? sceneEntry.backGLBInfo?.codigo ?? ''),
        },
        glbSceneChildren,
        acabadosZonasExtra: buildAcabadosZonasExtra(acabados),
        itemId,
        source: 'projectLoad:remapStaleGlbZones',
      })
      result.remappedCount += 1
      result.remappedItemIds.push(itemId)
    } catch (error) {
      console.warn(
        `[remapStaleLinkedZonesOnProjectLoad] Fallo remapeando zonas de ${itemId}`,
        error,
      )
    }
  }

  return result
}
